import {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {PageHeader} from '@nimbitlabs/layouts';
import {BookOpen, Play, Plus, Edit, Clock, Trash2} from 'lucide-react';
import {ErrorAlert} from '@nimbitlabs/ui';
import {PermissionGate} from '@nimbitlabs/features';
import {useReadOnlyMode} from '@nimbitlabs/features';
import {Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, useToast} from '@nimbitlabs/ui';
import {useRunbooksStore} from '@/stores/runbooksStore';

export default function Runbooks() {
    const navigate = useNavigate();
    const {runbooks, loading, error, fetchRunbooks, executeRunbook, deleteRunbook} = useRunbooksStore();
    const {toast} = useToast();
    const {isReadOnly} = useReadOnlyMode();

    useEffect(() => {
        fetchRunbooks();
    }, [fetchRunbooks]);

    const handleExecute = async (runbookId: string) => {
        try {
            await executeRunbook(runbookId);
            toast({title: 'Success', description: 'Runbook executed successfully'});
        } catch (err) {
            toast({
                title: 'Error',
                description: 'Failed to execute runbook',
                variant: 'destructive'
            });
        }
    };

    const handleEdit = (runbookId: string) => {
        navigate(`/runbooks/${runbookId}/edit`);
    };

    const handleDelete = async (runbookId: string, runbookName: string) => {
        if (!confirm(`Are you sure you want to delete "${runbookName}"? This action cannot be undone.`)) {
            return;
        }

        try {
            await deleteRunbook(runbookId);
            toast({
                title: 'Success',
                description: 'Runbook deleted successfully'
            });
        } catch (err) {
            toast({
                title: 'Error',
                description: 'Failed to delete runbook',
                variant: 'destructive'
            });
        }
    };

    return (
        <div className="space-y-3 md:space-y-6 px-2 md:px-0">
            <PageHeader
                title="Runbooks & Automation"
                description="Automated playbooks for common operations"
                actions={
                    <PermissionGate permission="runbooks:create">
                        <Button
                            className="w-full sm:w-auto"
                            onClick={() => navigate('/runbooks/new')}
                        >
                            <Plus className="h-4 w-4 mr-2"/>
                            Create Runbook
                        </Button>
                    </PermissionGate>
                }
            />

            {/* Error Display */}
            <ErrorAlert error={error}/>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {Array.from({length: 3}).map((_, i) => (
                        <Card key={i} className="border-l-4 border-l-primary animate-pulse">
                            <CardHeader className="pb-2">
                                <div className="h-4 bg-muted rounded w-24"/>
                            </CardHeader>
                            <CardContent>
                                <div className="h-10 bg-muted rounded w-16"/>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-3">
                    <Card className="relative overflow-hidden">
                        <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-3">
                            <CardTitle className="text-sm font-medium text-muted-foreground">Total Runbooks</CardTitle>
                            <div className="p-2 rounded-lg bg-blue-500/10">
                                <BookOpen className="h-5 w-5 text-blue-600" />
                            </div>
                        </CardHeader>
                        <CardContent className="relative">
                            <div className="text-4xl font-bold mb-1">
                                {runbooks.length}
                            </div>
                            <p className="text-sm text-muted-foreground">Active playbooks</p>
                        </CardContent>
                    </Card>

                    <Card className="relative overflow-hidden">
                        <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-3">
                            <CardTitle className="text-sm font-medium text-muted-foreground">Total Executions</CardTitle>
                            <div className="p-2 rounded-lg bg-purple-500/10">
                                <Play className="h-5 w-5 text-purple-600" />
                            </div>
                        </CardHeader>
                        <CardContent className="relative">
                            <div className="text-4xl font-bold mb-1">
                                {runbooks.reduce((sum, r) => sum + r.usageCount, 0)}
                            </div>
                            <p className="text-sm text-muted-foreground">Times executed</p>
                        </CardContent>
                    </Card>

                    <Card className="relative overflow-hidden">
                        <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-3">
                            <CardTitle className="text-sm font-medium text-muted-foreground">Automated Steps</CardTitle>
                            <div className="p-2 rounded-lg bg-green-500/10">
                                <Clock className="h-5 w-5 text-green-600" />
                            </div>
                        </CardHeader>
                        <CardContent className="relative">
                            <div className="text-4xl font-bold mb-1">
                                {runbooks.reduce((sum, r) => sum + (r.steps || []).filter(s => s.automated).length, 0)}
                            </div>
                            <p className="text-sm text-muted-foreground">Automated actions</p>
                        </CardContent>
                    </Card>
                </div>
            )}

            <div className="grid gap-6 md:grid-cols-1 xl:grid-cols-2">
                {loading ? (
                    Array.from({length: 4}).map((_, i) => (
                        <Card key={i} className="animate-pulse border-l-4 border-l-primary flex flex-col">
                            <CardHeader>
                                <div className="space-y-3">
                                    <div className="h-6 bg-muted rounded w-3/4"/>
                                    <div className="h-4 bg-muted rounded w-1/2"/>
                                    <div className="flex gap-2">
                                        <div className="h-6 bg-muted rounded w-20"/>
                                        <div className="h-6 bg-muted rounded w-16"/>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="h-4 bg-muted rounded"/>
                                <div className="h-4 bg-muted rounded w-2/3"/>
                            </CardContent>
                        </Card>
                    ))
                ) : (
                    runbooks.map((runbook) => (
                        <Card key={runbook.id} className="border-l-4 border-l-primary flex flex-col">
                            <CardHeader className="pb-4">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="space-y-3 flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <BookOpen className="h-5 w-5 text-primary flex-shrink-0"/>
                                            <CardTitle className="text-xl truncate">{runbook.name}</CardTitle>
                                        </div>
                                        <CardDescription
                                            className="text-sm leading-relaxed">{runbook.description}</CardDescription>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <Badge variant="secondary" className="text-xs">{runbook.serviceName}</Badge>
                                            <Badge variant="outline" className="text-xs">
                                                {(runbook.steps || []).length} {(runbook.steps || []).length === 1 ? 'step' : 'steps'}
                                            </Badge>
                                            {(runbook.steps || []).some(s => s.automated) && (
                                                <Badge
                                                    className="text-xs bg-primary/10 text-primary hover:bg-primary/20">
                                                    Automated
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex gap-2 flex-shrink-0">
                                        <PermissionGate permission="runbooks:update">
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => handleEdit(runbook.id)}
                                                className="h-8 w-8 p-0"
                                                title="Edit runbook"
                                            >
                                                <Edit className="h-4 w-4"/>
                                            </Button>
                                        </PermissionGate>
                                        <PermissionGate permission="runbooks:delete">
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => handleDelete(runbook.id, runbook.name)}
                                                className="h-8 w-8 p-0 hover:bg-destructive/10 hover:text-destructive"
                                                title="Delete runbook"
                                                disabled={isReadOnly}
                                            >
                                                <Trash2 className="h-4 w-4"/>
                                            </Button>
                                        </PermissionGate>
                                    </div>
                                </div>
                            </CardHeader>

                            <CardContent className="space-y-6 flex-1">
                                <div className="space-y-2">
                                    <h4 className="text-sm font-semibold text-foreground/80">Trigger Condition</h4>
                                    <code
                                        className="block text-xs bg-muted/50 px-3 py-2 rounded-md border text-foreground/90">
                                        {runbook.trigger}
                                    </code>
                                </div>

                                <div className="space-y-3">
                                    <h4 className="text-sm font-semibold text-foreground/80">Execution Steps</h4>
                                    <div className="space-y-3">
                                        {(runbook.steps || []).map((step, index) => (
                                            <div key={step.id}
                                                 className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/50">
                                                <div
                                                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 border border-primary/20 text-xs font-semibold text-primary">
                                                    {index + 1}
                                                </div>
                                                <div className="flex-1 min-w-0 space-y-1">
                                                    <div
                                                        className="font-medium text-sm text-foreground">{step.title}</div>
                                                    <div
                                                        className="text-xs text-muted-foreground leading-relaxed">{step.description}</div>
                                                    {step.automated && (
                                                        <Badge variant="outline" className="mt-1.5 text-xs h-5">
                                                            Auto-execute
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </CardContent>

                            <CardContent className="pt-0 pb-6">
                                <div className="flex items-center justify-between pt-4 border-t">
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                        <Clock className="h-3.5 w-3.5"/>
                                        {runbook.lastUsed ? (
                                            <span>Last used {new Date(runbook.lastUsed).toLocaleDateString()}</span>
                                        ) : (
                                            <span>Never used</span>
                                        )}
                                    </div>

                                    <div className="flex gap-2 flex-shrink-0">
                                        <PermissionGate permission="runbooks:update">
                                            <Button
                                                size="sm"
                                                onClick={() => handleExecute(runbook.id)}
                                                disabled={isReadOnly}
                                            >
                                                <Play className="h-4 w-4 mr-2"/>
                                                Execute
                                            </Button>
                                        </PermissionGate>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>

            {!loading && runbooks.length === 0 && (
                <Card>
                    <CardContent className="py-12 text-center">
                        <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4"/>
                        <h3 className="font-semibold mb-2">No runbooks yet</h3>
                        <p className="text-sm text-muted-foreground">
                            Create runbooks to automate common operations and incident response
                        </p>
                    </CardContent>
                </Card>
            )}

        </div>
    );
}
